import API_URLS from '~/core/api-urls'
import cacheService from '../core/cache'
import { DOCK_IPC } from '../core/constants'
import api from './api'
import { getCurrentInstance, disableAlwaysOnTop, enableAlwaysOnTop } from './alwaysontop'
import { getCurrentApi, sendIPCMessage } from './jitsi-api'
import { isLegacyAotMode } from './jitsi-sdk'

// whether to show a notification when the meeting is undocked, to inform the user that they can redock the meeting
const IS_UNDOCKED_ENABLED = 'meeting-undocked-enabled'

/**
 * Cache key for the Always On Top / Picture-in-Picture enabled setting.
 *
 * NOTE: This cache key is used for both legacy AOT (Always On Top) and new PiP (Picture-in-Picture) features.
 * The implementation auto-detects which mode to use based on jitsiMeetElectronUtils capabilities.
 *
 * TODO: Rename to 'jitsi-preview-enabled' once legacy AOT support is completely removed.
 */
const IS_ALWAYSONTOP_ENABLED = 'jitsi-alwaysontop-enabled'

let undockedCallback

export const getIsUndockedEnabled = () => new Promise(resolve => {
  cacheService.cache.get(IS_UNDOCKED_ENABLED, data => {
    let flag = true
    if (data && data[IS_UNDOCKED_ENABLED]) {
      flag = data[IS_UNDOCKED_ENABLED].value
    }

    resolve(flag)
  })
})

export const setIsUndockedEnabled = value => {
  cacheService.cache.set({ [IS_UNDOCKED_ENABLED]: { value } })
}

const getIsAlwaysOnTopEnabled = () => new Promise(resolve => {
  cacheService.cache.get(IS_ALWAYSONTOP_ENABLED, data => {
    let flag = true
    if (data && data[IS_ALWAYSONTOP_ENABLED]) {
      flag = data[IS_ALWAYSONTOP_ENABLED].value
    }

    resolve(flag)
  })
})

const setIsAlwaysOnTopEnabled = (value, isUndocked = false) => {
  cacheService.cache.set({ [IS_ALWAYSONTOP_ENABLED]: { value } })

  // Try to update runtime state
  const instance = getCurrentInstance()
  const api = getCurrentApi()

  // Legacy AOT mode: Use API proxy symbols
  if (instance && instance[enableAlwaysOnTop]) {
    if (value) {
      instance[enableAlwaysOnTop]()
    } else {
      instance[disableAlwaysOnTop]()
    }
    return
  }

  // If undocked, send IPC message to undocked window (only for PiP mode, not legacy AOT)
  if (isUndocked) {
    // In legacy AOT mode, the setting is saved but PiP should stay disabled
    if (isLegacyAotMode()) {
      return
    }

    sendIPCMessage({
      type: DOCK_IPC.UPDATE_PIP_CONFIG,
      enabled: value
    })
    return
  }

  // NEW PiP mode: Use overwriteConfig command
  if (api) {
    // We're in PiP mode - update config dynamically
    api.executeCommand('overwriteConfig', {
      pip: {
        disabled: !value, // Invert: value is "enabled", config needs "disabled"
        showOnPrejoin: true
      }
    })
  }

  // If neither instance nor api exists, the setting is saved but won't take effect until next meeting
}

export const initExternalNotifyUndocked = externalUndockedCallback => {
  if (externalUndockedCallback) {
    undockedCallback = externalUndockedCallback
  }
}

export const externalNotifyUndockedChanged = isUndocked => undockedCallback?.(isUndocked)

const updatePersonalRoomName = name =>
  api.post(API_URLS.personalConference, {
    name
  })

const updatePersonalRoomPassword = passcode =>
  api.put(API_URLS.personalConferencePasscode, {
    passcode
  })

const removePersonalRoomPassword = () =>
  api.del(API_URLS.personalConferencePasscode)

const fetchMeetingProperties = meetingFqn =>
  api.get(API_URLS.getMeetingProperties(meetingFqn))

const updateMeetingProperties = properties =>
  api.patch(API_URLS.meetingProperties, properties)

export const fetchAppContacts = query =>
  api.get(API_URLS.contacts(query))

export default {
  getIsAlwaysOnTopEnabled,
  setIsAlwaysOnTopEnabled,
  updatePersonalRoomName,
  updatePersonalRoomPassword,
  removePersonalRoomPassword,
  fetchMeetingProperties,
  updateMeetingProperties,
  fetchAppContacts
}
